/**
 * Stale-deploy chunk recovery.
 *
 * A deploy renames the lazy chunks. A tab opened before it asks for the old
 * names, the server has deleted them, and the route never loads: the screen
 * freezes until someone refreshes. One reload fetches the new index.html and
 * chunk map.
 *
 * - The reload happens once per {@link CHUNK_RELOAD_GUARD_MS}, tracked in
 *   sessionStorage, so a chunk that is genuinely broken cannot loop. With no
 *   working storage there is no reload at all.
 * - With a service worker in control, it is asked for the new build first
 *   (bounded by {@link SW_UPDATE_WAIT_MS}). Cache Storage is never touched:
 *   that would corrupt the worker's own caches.
 *
 * Wired in `app.config.ts` through `AnalyticsErrorHandler` (errors Angular
 * catches) and `withNavigationErrorHandler` (failed lazy-route navigations).
 */
import { DOCUMENT } from '@angular/common';
import { Injectable, InjectionToken, inject } from '@angular/core';
import type { NavigationError } from '@angular/router';
import { SwUpdate } from '@angular/service-worker';

export const CHUNK_RELOAD_AT_KEY = 'login-web-chunk-reload-at';

/** A second chunk failure inside this window is reported, not reloaded. */
export const CHUNK_RELOAD_GUARD_MS = 2 * 60 * 1000;

/** The longest the reload waits for the service worker to fetch the new build. */
export const SW_UPDATE_WAIT_MS = 4000;

/** The page's own navigation. A token so tests never reload the runner. */
export const PAGE_LOCATION = new InjectionToken<Pick<Location, 'reload'>>('PAGE_LOCATION', {
  providedIn: 'root',
  factory: () => inject(DOCUMENT).location,
});

const CHUNK_ERROR_PATTERN =
  /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|ChunkLoadError|Loading chunk \d+ failed/i;

/** True when the error is a lazy chunk that could not be fetched. */
export function isChunkLoadError(err: unknown): boolean {
  const message =
    err && typeof err === 'object' && 'message' in err
      ? String((err as { message?: unknown }).message ?? '')
      : String(err ?? '');
  return CHUNK_ERROR_PATTERN.test(message);
}

@Injectable({ providedIn: 'root' })
export class ChunkReloadRecovery {
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly page = inject(PAGE_LOCATION);

  /**
   * Starts a guarded reload when `error` is a chunk-load failure. Returns true
   * when a reload is under way, so the caller can skip reporting it.
   */
  recover(error: unknown): boolean {
    if (!isChunkLoadError(error)) return false;
    if (!this.claimReload()) return false;
    void this.reloadOntoNewBuild();
    return true;
  }

  /** Records the attempt. False when one ran recently or storage is unusable. */
  private claimReload(): boolean {
    try {
      const marker = sessionStorage.getItem(CHUNK_RELOAD_AT_KEY);
      const now = Date.now();
      // An unparseable marker is treated as recent: never risk a loop.
      if (marker !== null) {
        const last = Number(marker);
        if (!Number.isFinite(last) || now - last <= CHUNK_RELOAD_GUARD_MS) return false;
      }
      sessionStorage.setItem(CHUNK_RELOAD_AT_KEY, String(now));
      return true;
    } catch {
      return false;
    }
  }

  private async reloadOntoNewBuild(): Promise<void> {
    if (this.swUpdate?.isEnabled && this.hasControllingWorker()) {
      let timer: ReturnType<typeof setTimeout> | undefined;
      const timeout = new Promise<void>((resolve) => {
        timer = setTimeout(resolve, SW_UPDATE_WAIT_MS);
      });
      await Promise.race([this.swUpdate.checkForUpdate().catch(() => false), timeout]);
      clearTimeout(timer);
    }
    this.page.reload();
  }

  private hasControllingWorker(): boolean {
    try {
      return !!navigator.serviceWorker?.controller;
    } catch {
      return false;
    }
  }
}

/** For `withNavigationErrorHandler`: reload for a stale lazy route. */
export function chunkReloadNavigationError(event: NavigationError): void {
  inject(ChunkReloadRecovery).recover(event.error);
}
